<template>
  <div class="user-avatar" :class="classes" @click="handleProfileClick">
    <div v-if="online" class="live-indicator">
      <div class="inner-circle"></div>
    </div>
    <div v-if="hasStatusIcon" class="status">
      <svg-icon :name="statusIcon" kind="spaces" />
    </div>
    <SalisAvatar24 v-if="showSystemAvatar" class="user-icon" />
    <img v-else-if="showUserAvatar" :src="userAvatarUrl" class="user-icon" />
    <svg-icon v-else-if="showRoleIcon" :name="roleIcon" kind="spaces" class="user-icon" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import ProfileContentSubpage from "@modules/profile/components/ProfileContentSubpage.vue";
import SalisAvatar24 from "@images/learning-assistant/SalisAvatar24.svg?component";
import { isEmpty } from "@utils/var.check";
import { userOnlineService } from "@plugins/common/service/user.online.service.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";

// Props
interface Props {
  userAvatarUrl?: string;
  roleIcon?: string;
  statusIcon?: string;
  isGameAvatar?: boolean;
  userId: number;
  openProfileSubpageWhenClicked?: boolean;
}

const {
  userAvatarUrl = "",
  roleIcon = "",
  statusIcon = "",
  isGameAvatar = false,
  userId,
  openProfileSubpageWhenClicked = true,
} = defineProps<Props>();

const ONLINE_STATUS_INTERVAL = 1 * 60 * 1000; // 1 minute

// Composables
const contentSubpageInstance = useContentSubpageInstance();

// Data
const online = ref(false);
let timeoutId: number | null = null;

// Computed
const hasStatusIcon = computed(() => statusIcon && statusIcon !== "");

const showSystemAvatar = computed(() => userId === 1);

const showUserAvatar = computed(() => userAvatarUrl !== "");

const showRoleIcon = computed(() => !showUserAvatar.value && roleIcon !== "");

const classes = computed(() => ({
  viewing: statusIcon === "Eye16",
  collaborating: statusIcon === "Bulb16",
  "aggregated-view": statusIcon === "AggregatedView16",
  "_has-status-icon": hasStatusIcon.value,
  "game-avatar": isGameAvatar,
  "system-avatar": showSystemAvatar.value,
}));

// Methods
const checkUserOnlineStatus = () => {
  // TODO: Let value for userOnlineService.isUserOnline(String(userId)) to be reactive
  online.value = userOnlineService.isUserOnline(String(userId));
};

const startInterval = () => {
  let delay = 500; // Start with 0.5 second

  const check = () => {
    checkUserOnlineStatus();
    if (delay < ONLINE_STATUS_INTERVAL) {
      delay = Math.min(delay * 2, ONLINE_STATUS_INTERVAL); // double the delay until 1 minutes
    }
    timeoutId = setTimeout(check, delay);
  };

  check();
};

const stopInterval = () => {
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
};

const handleProfileClick = (event: Event) => {
  if (showSystemAvatar.value) return;

  event.stopPropagation();

  if (!isEmpty(userId) && openProfileSubpageWhenClicked) {
    contentSubpageInstance.show(ProfileContentSubpage, {
      userId: userId,
      hasParentSubpage: !isEmpty(contentSubpageInstance.getCurrentBreadcrumb()),
      baseTitle: contentSubpageInstance.getCurrentBreadcrumb(),
    });
  }
};

// Lifecycle
onMounted(() => {
  if (showSystemAvatar.value) return;
  startInterval();
});

onUnmounted(() => {
  stopInterval();
});
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

$base-circle-size-min: 8px;
$base-circle-size-max: 28px;
.user-avatar {
  display: flex;
  align-items: center;
  align-self: center;
  justify-content: center;
  position: relative;
  width: 2.5rem;
  height: 2.5rem;
  margin: 0 auto;
  cursor: pointer;

  // to ensure focus style is consistent to the avatar on header when navigated via keyboard
  .app-header & {
    width: 3rem;
    height: 3rem;
    padding: $spacing-02;
    margin: 0 $spacing-02;

    .live-indicator {
      top: 4px;
      right: 4px;
      width: 25%;
      height: 25%;
    }
  }

  &.system-avatar {
    pointer-events: none;
    cursor: default;
  }

  &.game-avatar {
    img {
      width: 100%;
      object-fit: cover;
      border-radius: 50%;
    }
  }

  .live-indicator {
    position: absolute;
    top: 0;
    right: 0;
    height: 30%;
    width: 30%;
    min-width: calc($base-circle-size-min * 1.2);
    min-height: calc($base-circle-size-min * 1.2);
    max-width: calc($base-circle-size-max * 1.2);
    max-height: calc($base-circle-size-max * 1.2);
    border-radius: 50%;
    background-color: $ui-01;
    z-index: z-map("default");

    .inner-circle {
      position: absolute;
      top: 50%;
      left: 50%;
      height: 83.3%;
      width: 83.3%;
      transform: translate(-50%, -50%);
      min-width: $base-circle-size-min;
      min-height: $base-circle-size-min;
      max-width: $base-circle-size-max;
      max-height: $base-circle-size-max;
      background-color: $status-online;
      border-radius: 100%;
    }
  }

  img {
    height: 100%;
    width: auto;
  }

  .status {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.25rem;
    height: 1.25rem;
    position: absolute;
    top: (-$spacing-01);
    left: (-$spacing-03);
    border-radius: 50%;
    z-index: 1;
  }

  &._has-status-icon {
    padding: $spacing-01;
    border-radius: 50%;
  }

  &.aggregated-view {
    background-color: $status-aggregated;

    .status {
      background-color: $status-aggregated;

      .sls-icon {
        &[name] {
          path {
            &[fill] {
              fill: $state-info-text;
            }
          }
        }
      }
    }
  }

  &.viewing {
    background-color: $status-viewing;

    .status {
      background-color: $status-viewing;

      .sls-icon {
        &[name] {
          path {
            &[fill] {
              fill: tint($icon-01, 20%);
            }
          }
        }
      }
    }
  }

  &.collaborating {
    background-color: $status-collaborating;

    .status {
      background-color: $status-collaborating;

      .sls-icon {
        &[name] {
          path {
            &[fill] {
              fill: $icon-03;
            }
          }
        }
      }
    }
  }
}
</style>
